import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { APISource } from '@/lib/types';
import { LogoService, LogoSource } from '@/lib/logoService';
import { useEffect, useState } from 'react';
import {
  ChartLine,
  Cloud,
  Code,
  Database,
  Globe,
  Heart,
  Key,
  Leaf,
  Lock,
  MapPin,
  MusicNote,
  Newspaper,
  Users,
} from '@phosphor-icons/react';

interface APISourceCardProps {
  source: APISource;
  onSelect: (source: APISource) => void;
}

export function APISourceCard({ source, onSelect }: APISourceCardProps) {
  const [logoError, setLogoError] = useState(false);
  const [fetchedLogo, setFetchedLogo] = useState<LogoSource | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(false);

  // Fetch logo on component mount
  useEffect(() => {
    const fetchLogo = async () => {
      if (!source.organization) {
        return;
      }

      setIsLoadingLogo(true);
      try {
        const result = await LogoService.searchLogos(
          source.organization,
          source.domain
        );
        const bestLogo = LogoService.getBestLogo(result);

        setFetchedLogo(bestLogo);
      } catch (error) {
        console.debug('Logo fetch failed:', error);
      } finally {
        setIsLoadingLogo(false);
      }
    };

    fetchLogo();
  }, [source.organization, source.domain]);

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'government':
        return <Users className='w-4 h-4' />;
      case 'economics':
        return <ChartLine className='w-4 h-4' />;
      case 'environment':
        return <Leaf className='w-4 h-4' />;
      case 'health':
      case 'healthcare':
        return <Heart className='w-4 h-4' />;
      case 'technology':
        return <Code className='w-4 h-4' />;
      case 'entertainment':
        return <MusicNote className='w-4 h-4' />;
      case 'media':
        return <Newspaper className='w-4 h-4' />;
      default:
        return <Database className='w-4 h-4' />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'government':
        return 'text-blue-600 bg-blue-50';
      case 'economics':
        return 'text-orange-600 bg-orange-50';
      case 'environment':
        return 'text-green-600 bg-green-50';
      case 'health':
      case 'healthcare':
        return 'text-red-600 bg-red-50';
      case 'technology':
        return 'text-purple-600 bg-purple-50';
      case 'entertainment':
        return 'text-pink-600 bg-pink-50';
      case 'media':
        return 'text-indigo-600 bg-indigo-50';
      default:
        return 'text-primary bg-primary/10';
    }
  };

  const getPopularityColor = (popularity: number) => {
    if (popularity >= 90) {
      return 'bg-insight-green text-white';
    }
    if (popularity >= 75) {
      return 'bg-analysis-orange text-white';
    }

    return 'bg-muted text-muted-foreground';
  };

  const getPlaceholderLogo = (organization: string) => {
    const firstLetter = organization?.charAt(0) || source.name.charAt(0);
    const colors = [
      'bg-blue-500 text-white',
      'bg-green-500 text-white',
      'bg-purple-500 text-white',
      'bg-orange-500 text-white',
      'bg-red-500 text-white',
      'bg-indigo-500 text-white',
    ];
    const colorIndex = source.name.charCodeAt(0) % colors.length;

    return (
      <div
        className={`w-10 h-10 rounded-lg ${colors[colorIndex]} flex items-center justify-center font-bold text-sm`}
      >
        {firstLetter.toUpperCase()}
      </div>
    );
  };

  const LogoOrIcon = () => {
    // Priority 1: Fetched logo from API (only if not failed)
    if (fetchedLogo && !logoError) {
      return (
        <div className='w-10 h-10 rounded-lg overflow-hidden bg-white border shadow-sm flex items-center justify-center'>
          <img
            src={fetchedLogo.url}
            alt={`${source.organization || source.name} logo`}
            className='w-8 h-8 object-contain'
            onError={() => {
              setLogoError(true);
              setFetchedLogo(null); // Remove failed logo from state
            }}
          />
        </div>
      );
    }

    // Priority 2: Manual logoUrl from source
    if (source.logoUrl && !logoError) {
      return (
        <div className='w-10 h-10 rounded-lg overflow-hidden bg-white border shadow-sm flex items-center justify-center'>
          <img
            src={source.logoUrl}
            alt={`${source.organization || source.name} logo`}
            className='w-8 h-8 object-contain'
            onError={() => setLogoError(true)}
          />
        </div>
      );
    }

    // Priority 3: Loading state for logo fetch
    if (isLoadingLogo && source.organization) {
      return (
        <div className='w-10 h-10 rounded-lg bg-muted border animate-pulse flex items-center justify-center'>
          <div className='w-5 h-5 bg-muted-foreground/20 rounded'></div>
        </div>
      );
    }

    // Priority 4: Organization placeholder logo
    if (source.organization) {
      return getPlaceholderLogo(source.organization);
    }

    // Priority 5: Fall back to category icon
    return (
      <div className={`p-2 rounded-lg ${getCategoryColor(source.category)}`}>
        {getCategoryIcon(source.category)}
      </div>
    );
  };

  return (
    <Card className='hover:shadow-lg transition-all duration-200 border hover:border-primary/20 group h-[320px] flex flex-col overflow-hidden'>
      <CardHeader className='pb-3 flex-shrink-0'>
        <div className='flex items-start gap-3 min-w-0'>
          {/* Organization logo or category icon */}
          <div className='flex-shrink-0'>
            <LogoOrIcon />
          </div>

          <div className='flex-1 min-w-0 overflow-hidden'>
            <CardTitle className='text-base font-semibold leading-tight group-hover:text-primary transition-colors mb-1 h-[2.5rem] flex items-start overflow-hidden'>
              <span className='line-clamp-2 break-words'>{source.name}</span>
            </CardTitle>
            {source.organization && (
              <div className='text-xs text-muted-foreground font-medium h-4 flex items-center overflow-hidden'>
                <span className='truncate'>{source.organization}</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className='pt-0 pb-4 flex-1 flex flex-col min-h-0 overflow-hidden'>
        {/* Content area with flexible height */}
        <div className='flex-1 space-y-3 min-h-0 overflow-hidden'>
          <div className='flex items-center justify-between h-4 min-w-0'>
            <div className='flex items-center gap-1.5 text-xs text-muted-foreground min-w-0 overflow-hidden'>
              {getCategoryIcon(source.category)}
              <span className='truncate'>{source.category}</span>
            </div>
            <div className='flex items-center gap-1.5 flex-shrink-0'>
              <div
                className={`w-2 h-2 rounded-full ${
                  source.popularity >= 90
                    ? 'bg-green-500'
                    : source.popularity >= 75
                      ? 'bg-orange-500'
                      : 'bg-gray-400'
                }`}
              />
              <span className='text-xs font-medium'>{source.popularity}%</span>
            </div>
          </div>

          <div className='flex items-center justify-between h-4 min-w-0'>
            <div
              className='flex items-center gap-1.5 text-xs text-muted-foreground cursor-help group/datasets'
              title={`Datasets: ${source.datasets.map(d => d.name).join(', ')}`}
            >
              <Database className='w-3 h-3 flex-shrink-0' />
              <span className='whitespace-nowrap'>
                {source.datasets.length} datasets
              </span>
              <div className='opacity-0 group-hover/datasets:opacity-100 transition-opacity'>
                <Badge
                  variant='outline'
                  className='text-xs scale-75 origin-left'
                >
                  Hover for list
                </Badge>
              </div>
            </div>
          </div>

          {/* Auth Required Section - moved up from button area for better visibility */}
          {source.authRequired && (source.authUrl || source.docsUrl) && (
            <div className='bg-accent/5 border border-accent/20 rounded-md p-2 space-y-1.5'>
              <div className='flex items-center gap-1.5 text-xs font-medium text-accent'>
                <Key className='w-3 h-3' />
                <span>API Key Required</span>
              </div>
              <div className='flex gap-1.5'>
                {source.authUrl && (
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-6 px-2 py-0 text-xs'
                    onClick={e => {
                      e.stopPropagation();
                      window.open(source.authUrl, '_blank');
                    }}
                  >
                    Get Key
                  </Button>
                )}
                {source.docsUrl && (
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-6 px-2 py-0 text-xs'
                    onClick={e => {
                      e.stopPropagation();
                      window.open(source.docsUrl, '_blank');
                    }}
                  >
                    Docs
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Description or additional info if available */}
          {source.description && (
            <div className='text-xs text-muted-foreground leading-relaxed overflow-hidden'>
              <div
                className={`${source.authRequired && (source.authUrl || source.docsUrl) ? 'line-clamp-2' : 'line-clamp-3'} break-words`}
              >
                {source.description}
              </div>
            </div>
          )}
        </div>

        {/* Fixed button at bottom - always positioned at the bottom */}
        <div className='flex-shrink-0 pt-3'>
          <Button
            onClick={() => onSelect(source)}
            className='w-full group-hover:shadow-md transition-all h-9'
            size='sm'
          >
            <MapPin className='w-3 h-3 mr-1.5' />
            Explore
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
